/**
 * Better TDL Logo Definition
 *
 * This is the SINGLE SOURCE OF TRUTH for the Better TDL logo.
 * Used by:
 * - Logo.tsx component for rendering in the app
 * - scripts/generate-assets.js for generating favicon and PNG assets
 *
 * The logo represents a constellation network - an organic web of interconnected tasks
 * with a central hub showing complex task relationships. Featuring glowing nodes,
 * flowing curves, and a sophisticated dark cosmic theme that captures the beauty
 * and complexity of hierarchical task management.
 */

export const LOGO_VIEWBOX = '0 0 124 124';

/**
 * Get the complete SVG content (without outer svg tag)
 * Used by Logo component via dangerouslySetInnerHTML
 */
export function getLogoContent() {
  return `
  <defs>
    <linearGradient id="bgGrad" x1="0" y1="0" x2="124" y2="124" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="#020617"/>
      <stop offset="50%" stop-color="#0f172a"/>
      <stop offset="100%" stop-color="#1e293b"/>
    </linearGradient>
    <linearGradient id="lineGrad" x1="30" y1="30" x2="94" y2="94" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="#6366f1" stop-opacity="0.6"/>
      <stop offset="50%" stop-color="#06b6d4" stop-opacity="0.7"/>
      <stop offset="100%" stop-color="#10b981" stop-opacity="0.8"/>
    </linearGradient>
    <radialGradient id="nodeGlow1" cx="0.5" cy="0.5" r="0.5">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="50%" stop-color="#e0f2fe"/>
      <stop offset="100%" stop-color="#a5b4fc"/>
    </radialGradient>
    <radialGradient id="nodeGlow2" cx="0.5" cy="0.5" r="0.5">
      <stop offset="0%" stop-color="#e0f2fe"/>
      <stop offset="50%" stop-color="#7dd3fc"/>
      <stop offset="100%" stop-color="#06b6d4"/>
    </radialGradient>
    <radialGradient id="nodeGlow3" cx="0.5" cy="0.5" r="0.5">
      <stop offset="0%" stop-color="#d1fae5"/>
      <stop offset="50%" stop-color="#6ee7b7"/>
      <stop offset="100%" stop-color="#10b981"/>
    </radialGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur"/>
      <feColorMatrix in="blur" type="matrix"
        values="0 0 0 0 0.4
                0 0 0 0 0.7
                0 0 0 0 1.0
                0 0 0 0.8 0" result="glowColor"/>
      <feMerge>
        <feMergeNode in="glowColor"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>

  <!-- Background -->
  <rect width="124" height="124" rx="28" fill="url(#bgGrad)"/>

  <!-- Connection lines (organic, constellation-like) -->
  <!-- Hub to spoke connections -->
  <path d="M 62 58 Q 50 45, 38 35" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>
  <path d="M 62 58 Q 70 40, 82 28" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>
  <path d="M 62 58 Q 80 58, 95 55" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>
  <path d="M 62 58 Q 75 75, 88 88" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>
  <path d="M 62 58 Q 62 78, 62 95" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>
  <path d="M 62 58 Q 45 70, 30 78" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>
  <path d="M 62 58 Q 42 55, 25 52" stroke="url(#lineGrad)" stroke-width="2" fill="none" opacity="0.6"/>

  <!-- Peer-to-peer connections (constellation effect) -->
  <path d="M 38 35 Q 60 30, 82 28" stroke="url(#lineGrad)" stroke-width="1.5" fill="none" opacity="0.4"/>
  <path d="M 95 55 Q 92 72, 88 88" stroke="url(#lineGrad)" stroke-width="1.5" fill="none" opacity="0.4"/>
  <path d="M 30 78 Q 40 88, 62 95" stroke="url(#lineGrad)" stroke-width="1.5" fill="none" opacity="0.4"/>
  <path d="M 25 52 Q 28 64, 30 78" stroke="url(#lineGrad)" stroke-width="1.5" fill="none" opacity="0.4"/>

  <!-- Nodes (varied sizes, glowing) -->
  <!-- Small nodes (incomplete) -->
  <g filter="url(#glow)">
    <circle cx="25" cy="52" r="6" fill="url(#nodeGlow1)" stroke="#8b5cf6" stroke-width="2"/>
  </g>

  <!-- Medium nodes -->
  <g filter="url(#glow)">
    <circle cx="38" cy="35" r="7" fill="url(#nodeGlow1)" stroke="#6366f1" stroke-width="2"/>
  </g>

  <g filter="url(#glow)">
    <circle cx="30" cy="78" r="7" fill="url(#nodeGlow2)" stroke="#14b8a6" stroke-width="2"/>
  </g>

  <!-- Medium-large nodes (checked) -->
  <g filter="url(#glow)">
    <circle cx="82" cy="28" r="8" fill="#06b6d4" stroke="#22d3ee" stroke-width="2.5"/>
    <path d="M 77 28 L 80 31 L 87 24" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>

  <g filter="url(#glow)">
    <circle cx="95" cy="55" r="7" fill="#14b8a6" stroke="#2dd4bf" stroke-width="2"/>
    <path d="M 91 55 L 94 58 L 99 53" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </g>

  <g filter="url(#glow)">
    <circle cx="88" cy="88" r="8" fill="#10b981" stroke="#22c55e" stroke-width="2.5"/>
    <path d="M 83 88 L 86 91 L 93 84" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>

  <!-- Small node (incomplete) -->
  <g filter="url(#glow)">
    <circle cx="62" cy="95" r="6" fill="url(#nodeGlow3)" stroke="#10b981" stroke-width="2"/>
  </g>

  <!-- Central hub node (large, checked - the main completed task) -->
  <g filter="url(#glow)">
    <circle cx="62" cy="58" r="11" fill="#059669" stroke="#10b981" stroke-width="3"/>
    <path d="M 56 58 L 60 62 L 68 54" stroke="#ffffff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  `;
}

/**
 * Generate complete SVG markup with outer svg tag
 * Used by asset generation script to create favicon.svg and PNG files
 *
 * @param {boolean} includeBackground - Not used in this design (background is part of the logo)
 * @returns {string} Complete SVG markup
 */
export function generateLogoSVG(includeBackground = false) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${LOGO_VIEWBOX}" fill="none">
${getLogoContent()}
</svg>`;
}
